import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, ButtonBase, IconButton, InputAdornment, List, ListItemButton,
    ListItemText, Paper, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CodeIcon from '@mui/icons-material/Code';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
    EditorProvider, Editor as SimpleWysiwygEditor, Toolbar,
    BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough, BtnLink,
    BtnNumberedList, BtnBulletList, BtnUndo, BtnRedo, Separator,
} from 'react-simple-wysiwyg';

const LANGS = [
    { code: 'nl', short: 'NL', label: 'Dutch' },
    { code: 'en', short: 'GB', label: 'English' },
    { code: 'fr', short: 'FR', label: 'French' },
];
const POSITIONS = [
    { value: 'top',   label: 'Top of page' },
    { value: 'below', label: 'Below the menu' },
];

// Tiny pictogram for the position picker. A bordered rectangle with a coloured
// bar either at the top or in the middle.
const PositionIcon = ({ value, selected }) => {
    const accent = selected ? 'rgba(25,118,210,0.9)' : 'rgba(0,0,0,0.55)';
    return (
        <Box sx={{
            width: 38, height: 28,
            border: `1.5px solid ${accent}`,
            borderRadius: 0.6,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: value === 'top' ? 'flex-start' : 'center',
            alignItems: 'stretch',
            p: '3px',
            gap: '2px',
        }}>
            <Box sx={{ height: 4, backgroundColor: accent, borderRadius: 0.3 }} />
            {value === 'top' && (<>
                <Box sx={{ height: 2, backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 0.3 }} />
                <Box sx={{ height: 2, backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 0.3 }} />
            </>)}
        </Box>
    );
};
PositionIcon.propTypes = { value: PropTypes.string, selected: PropTypes.bool };

const isEmptyHtml = (html) => {
    if (!html) return true;
    const txt = String(html).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    return txt.length === 0;
};

const blankBanner = (i = 1) => ({
    id: `b_${Date.now()}_${i}`,
    referenceName: `Banner — ${i}`,
    isActive: true,
    activeFrom: '',
    activeTo: '',
    position: 'top',
    bgColor: '#000000',
    textColor: '#ffffff',
    height: 30,
    scrollText: false,
    multipleItems: false,
    editor: 'wysiwyg',
    content: { nl: '', en: '', fr: '' },
});

// Card — white panel with rounded border, padded section with title + desc.
// Children render in a vertical Stack with light dividers between them.
const Section = ({ title, description, children, divider = true }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
            {description && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{description}</Typography>}
        </Box>
        <Stack
            spacing={1.5}
            divider={divider ? <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} /> : null}
        >
            {children}
        </Stack>
    </Paper>
);
Section.propTypes = { title: PropTypes.node, description: PropTypes.node, children: PropTypes.node, divider: PropTypes.bool };

// Generic horizontal row: label + help text on the left, control on the right.
// Mirrors the schema's left/right rhythm in every section.
const Row = ({ label, help, control, align = 'center' }) => (
    <Box sx={{ display: 'flex', alignItems: align === 'top' ? 'flex-start' : 'center', gap: 2, py: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
);
Row.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node, align: PropTypes.oneOf(['top', 'center']) };

const BannersTab = ({ t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [banners, setBanners] = useState([
        { ...blankBanner(1), referenceName: 'Banner — 1', isActive: true,  activeFrom: '2025-10-31', activeTo: '2026-11-05', scrollText: true, multipleItems: true,
          content: { nl: 'Mid-season sale — tot 50% korting op geselecteerde items', en: 'Mid-season sale — up to 50% off selected items', fr: 'Soldes de mi-saison — jusqu\'à 50% sur une sélection' } },
        { ...blankBanner(2), referenceName: 'Banner — 2', isActive: true  },
        { ...blankBanner(3), referenceName: 'Banner — 3', isActive: false },
    ]);
    const [activeId, setActiveId] = useState(banners[0]?.id);
    const [langTab, setLangTab]   = useState(0);

    const active = banners.find(b => b.id === activeId);
    const activeCount = banners.filter(b => b.isActive).length;

    const updateActive = (patch) => setBanners(prev => prev.map(b => b.id === activeId ? { ...b, ...patch } : b));
    const updateActiveContent = (lang, html) => setBanners(prev => prev.map(b => b.id === activeId
        ? { ...b, content: { ...(b.content || {}), [lang]: html } }
        : b));
    const addBanner = () => {
        const next = blankBanner(banners.length + 1);
        setBanners(prev => [...prev, next]);
        setActiveId(next.id);
    };
    const removeBanner = () => {
        if (!active) return;
        const remaining = banners.filter(b => b.id !== activeId);
        setBanners(remaining);
        setActiveId(remaining[0]?.id);
    };
    const copyContentFrom = (fromLang) => {
        if (!active) return;
        const toLang = LANGS[langTab].code;
        if (fromLang === toLang) return;
        updateActiveContent(toLang, active.content?.[fromLang] || '');
    };
    const switchEditor = (mode) => updateActive({ editor: mode });

    const lang = LANGS[langTab].code;

    // The actual styled banner element used in the live preview.
    const renderPreviewBanner = () => {
        if (!active) return null;
        const html = active.content?.[lang] || '';
        const empty = isEmptyHtml(html);

        const inner = empty
            ? <Box component="span" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
                {tr('cfg_banners_preview_placeholder', 'Mid-season sale — up to 50% off selected items')}
            </Box>
            : <Box component="span" dangerouslySetInnerHTML={{ __html: html }} />;

        return (
            <Box sx={{
                backgroundColor: active.bgColor,
                color: active.textColor,
                height: Math.max(20, Number(active.height) || 30),
                display: 'flex', alignItems: 'center',
                justifyContent: active.scrollText ? 'flex-start' : 'center',
                fontSize: '0.85rem',
                px: 2,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                borderRadius: 0.75,
                '& p': { m: 0 },
                '& a': { color: 'inherit', textDecoration: 'underline' },
            }}>
                {active.scrollText ? (
                    <Box sx={{
                        display: 'inline-block',
                        animation: 'banner-marquee 12s linear infinite',
                        pl: '100%',
                        '@keyframes banner-marquee': {
                            '0%':   { transform: 'translateX(0)' },
                            '100%': { transform: 'translateX(-100%)' },
                        },
                    }}>{inner}</Box>
                ) : inner}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 3, alignItems: 'start' }}>
            {/* ─────────── Master ─────────── */}
            <Box>
                <Box sx={{ px: 1, mb: 1 }}>
                    <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                        {tr('cfg_banners_list_title', 'Information banners')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {banners.length} {tr('cfg_banners_count', 'banners')} · {activeCount} {tr('cfg_banners_active', 'active')}
                    </Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mx: 1, mb: 1 }} />
                <List dense disablePadding>
                    {banners.map((b) => (
                        <ListItemButton
                            key={b.id}
                            selected={b.id === activeId}
                            onClick={() => setActiveId(b.id)}
                            sx={{
                                borderRadius: 1.5, mb: 0.5, py: 1.25,
                                '&.Mui-selected': { backgroundColor: 'rgba(25,118,210,0.08)' },
                                '&.Mui-selected:hover': { backgroundColor: 'rgba(25,118,210,0.12)' },
                            }}
                        >
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: b.isActive ? 'success.main' : 'grey.400',
                                mr: 1.5, flexShrink: 0,
                            }} />
                            <ListItemText
                                primary={
                                    <Typography variant="body2" sx={{ fontWeight: b.id === activeId ? 700 : 500, color: b.id === activeId ? 'primary.main' : 'text.primary' }}>
                                        {b.referenceName}
                                    </Typography>
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>
                <ButtonBase
                    onClick={addBanner}
                    sx={{
                        mt: 1, width: '100%', py: 1.5, borderRadius: 1.5,
                        border: '1.5px dashed', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                        color: 'text.secondary',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                    }}
                >
                    <AddIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tr('cfg_banners_add', 'New banner')}
                    </Typography>
                </ButtonBase>
            </Box>

            {/* ─────────── Detail ─────────── */}
            {!active ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_banners_empty_select', 'Select a banner from the list, or add a new one to get started.')}
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2.5}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{active.referenceName}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {tr('cfg_banners_reference_lbl', 'Reference')}: {active.referenceName.replace('—', '-')}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined" color="error" size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={removeBanner}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            {tr('cfg_banners_delete', 'Delete banner')}
                        </Button>
                    </Box>

                    {/* Live preview — flat banner, no chrome */}
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.02)' }}>
                        <Typography variant="caption" sx={{
                            display: 'block', px: 2, py: 1,
                            color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5,
                        }}>
                            {tr('cfg_banners_live_preview', 'LIVE PREVIEW')}
                        </Typography>
                        <Box sx={{ px: 2, pb: 2 }}>
                            {renderPreviewBanner()}
                        </Box>
                    </Paper>

                    {/* Banner basics */}
                    <Section
                        title={tr('cfg_banners_basics_title', 'Banner basics')}
                        description={tr('cfg_banners_basics_desc', 'Identify and activate this banner.')}
                    >
                        <Row
                            label={tr('cfg_banners_reference_name', 'Reference name')}
                            help={tr('cfg_banners_reference_help', 'Internal name to identify this banner. Not visible to visitors.')}
                            control={
                                <TextField
                                    size="small"
                                    value={active.referenceName}
                                    onChange={(e) => updateActive({ referenceName: e.target.value })}
                                    sx={{ width: 220 }}
                                />
                            }
                        />
                        <Row
                            label={tr('cfg_banners_is_active', 'Banner is active')}
                            help={tr('cfg_banners_is_active_help', 'When on, the banner shows on the site between the dates set below')}
                            control={
                                <Switch color="success" checked={active.isActive} onChange={(e) => updateActive({ isActive: e.target.checked })} />
                            }
                        />
                        <Row
                            label={tr('cfg_banners_active_dates', 'Active dates')}
                            help={tr('cfg_banners_active_dates_help', 'The banner only shows during this date range. Useful for planning ahead — e.g. set the start date to the beginning of a sale period.')}
                            align="top"
                            control={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        type="date" size="small"
                                        value={active.activeFrom}
                                        onChange={(e) => updateActive({ activeFrom: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 160 }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_banners_dates_to', 'to')}</Typography>
                                    <TextField
                                        type="date" size="small"
                                        value={active.activeTo}
                                        onChange={(e) => updateActive({ activeTo: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 160 }}
                                    />
                                </Box>
                            }
                        />
                    </Section>

                    {/* Appearance */}
                    <Section
                        title={tr('cfg_banners_appearance_title', 'Appearance')}
                        description={tr('cfg_banners_appearance_desc', 'How the banner looks on the page.')}
                    >
                        <Row
                            label={tr('cfg_banners_position', 'Position')}
                            help={tr('cfg_banners_position_help', 'Where the banner appears on every page')}
                            control={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {POSITIONS.map(p => {
                                        const selected = active.position === p.value;
                                        return (
                                            <ButtonBase
                                                key={p.value}
                                                onClick={() => updateActive({ position: p.value })}
                                                sx={{
                                                    flexDirection: 'column', gap: 0.5,
                                                    p: 1.25, minWidth: 100,
                                                    borderRadius: 1.5,
                                                    border: '1.5px solid',
                                                    borderColor: selected ? 'primary.main' : 'divider',
                                                    backgroundColor: selected ? 'rgba(25,118,210,0.08)' : '#fff',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <PositionIcon value={p.value} selected={selected} />
                                                <Typography variant="caption" sx={{
                                                    fontWeight: 600,
                                                    color: selected ? 'primary.main' : 'text.secondary',
                                                    mt: 0.5,
                                                }}>
                                                    {tr(`cfg_banners_pos_${p.value}`, p.label)}
                                                </Typography>
                                            </ButtonBase>
                                        );
                                    })}
                                </Box>
                            }
                        />
                        <Row
                            label={tr('cfg_banners_colors_height', 'Colors and height')}
                            help={tr('cfg_banners_colors_height_help', "Background color, text color, and the banner's height")}
                            align="top"
                            control={
                                <Box sx={{
                                    display: 'flex', gap: 3, alignItems: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.03)',
                                    borderRadius: 1.5, p: 1.5,
                                }}>
                                    <ColorField
                                        label={tr('cfg_banners_bg', 'BACKGROUND')}
                                        value={active.bgColor}
                                        onChange={(v) => updateActive({ bgColor: v })}
                                    />
                                    <ColorField
                                        label={tr('cfg_banners_fg', 'TEXT COLOR')}
                                        value={active.textColor}
                                        onChange={(v) => updateActive({ textColor: v })}
                                    />
                                    <Box>
                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5 }}>
                                            {tr('cfg_banners_height_lbl', 'HEIGHT')}
                                        </Typography>
                                        <TextField
                                            type="number" size="small"
                                            value={active.height}
                                            onChange={(e) => updateActive({ height: Math.max(20, parseInt(e.target.value) || 0) })}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            sx={{ width: 110, backgroundColor: '#fff' }}
                                        />
                                    </Box>
                                </Box>
                            }
                        />
                        <Row
                            label={tr('cfg_banners_scroll', 'Scroll text from right to left')}
                            help={tr('cfg_banners_scroll_help', 'The banner text scrolls horizontally like a news ticker. Useful for longer messages.')}
                            control={
                                <Switch color="success" checked={active.scrollText} onChange={(e) => updateActive({ scrollText: e.target.checked })} />
                            }
                        />
                        <Row
                            label={tr('cfg_banners_multiple', 'Multiple items')}
                            help={tr('cfg_banners_multiple_help', 'When on, the content can hold multiple announcements that cycle through. When off, the banner shows a single message.')}
                            control={
                                <Switch color="success" checked={active.multipleItems} onChange={(e) => updateActive({ multipleItems: e.target.checked })} />
                            }
                        />
                    </Section>

                    {/* Banner content */}
                    <Section
                        title={tr('cfg_banners_content_title', 'Banner content')}
                        description={tr('cfg_banners_content_desc', 'The message visitors see. Each language has its own version.')}
                        divider={false}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                            <Tabs
                                value={langTab}
                                onChange={(_e, v) => setLangTab(v)}
                                sx={{
                                    minHeight: 36,
                                    '& .MuiTab-root': { minHeight: 36, px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.85rem', minWidth: 0 },
                                    '& .MuiTabs-indicator': { height: 2 },
                                }}
                            >
                                {LANGS.map((l) => (
                                    <Tab
                                        key={l.code}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 0.5 }}>{l.short}</Typography>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{l.label}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {tr('cfg_banners_copy_from', 'Copy content from:')}
                                </Typography>
                                {LANGS.filter((_, i) => i !== langTab).map((l) => (
                                    <Button
                                        key={l.code}
                                        size="small" variant="outlined"
                                        onClick={() => copyContentFrom(l.code)}
                                        sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 0, px: 1.25 }}
                                    >
                                        {l.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>

                        {active.editor === 'wysiwyg' ? (
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
                                '& .rsw-editor': { minHeight: 140, border: 'none' },
                                '& .rsw-toolbar': { borderBottom: '1px solid', borderColor: 'divider' },
                                '& .rsw-ce':     { minHeight: 100, padding: '12px 16px' },
                            }}>
                                <EditorProvider>
                                    <SimpleWysiwygEditor
                                        value={active.content?.[lang] || ''}
                                        onChange={(e) => updateActiveContent(lang, e.target.value)}
                                    >
                                        <Toolbar>
                                            <BtnBold />
                                            <BtnItalic />
                                            <BtnUnderline />
                                            <BtnStrikeThrough />
                                            <Separator />
                                            <BtnNumberedList />
                                            <BtnBulletList />
                                            <Separator />
                                            <BtnUndo />
                                            <BtnRedo />
                                            <Separator />
                                            <BtnLink />
                                        </Toolbar>
                                    </SimpleWysiwygEditor>
                                </EditorProvider>
                            </Box>
                        ) : (
                            <TextField
                                multiline minRows={4}
                                value={active.content?.[lang] || ''}
                                onChange={(e) => updateActiveContent(lang, e.target.value)}
                                fullWidth
                                placeholder="<span>Mid-season sale — up to <b>50%</b> off selected items</span>"
                                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                            />
                        )}

                        {/* Visual / Raw HTML — full-width 2-column toggle below the editor */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <EditorModeButton
                                active={active.editor === 'wysiwyg'}
                                onClick={() => switchEditor('wysiwyg')}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                label={tr('cfg_banners_use_visual', 'Visual editor')}
                            />
                            <EditorModeButton
                                active={active.editor === 'html'}
                                onClick={() => switchEditor('html')}
                                icon={<CodeIcon fontSize="small" />}
                                label={tr('cfg_banners_use_html', 'Raw HTML')}
                            />
                        </Box>
                    </Section>
                </Stack>
            )}
        </Box>
    );
};

const ColorField = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5 }}>
            {label}
        </Typography>
        <Box component="label" sx={{
            position: 'relative',
            display: 'block',
            width: 36, height: 36,
            borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.18)',
            backgroundColor: value,
            cursor: 'pointer',
        }}>
            <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
        </Box>
    </Box>
);
ColorField.propTypes = { label: PropTypes.string, value: PropTypes.string, onChange: PropTypes.func };

const EditorModeButton = ({ active, onClick, icon, label }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            py: 1.25, borderRadius: 1.5,
            border: '1.5px solid',
            borderColor: active ? 'primary.main' : 'divider',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : '#fff',
            color: active ? 'primary.main' : 'text.primary',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            fontWeight: 600,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main' },
        }}
    >
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </ButtonBase>
);
EditorModeButton.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, icon: PropTypes.node, label: PropTypes.node };

BannersTab.propTypes = { theme: PropTypes.string, t: PropTypes.func };
export default BannersTab;
